/**
 * Contacts tab of the business page (Business Account Pages slice 2).
 *
 * 1. Points of Contact (requirements p.3 §3.3, §6.3): several people per organization, each with a POC role, a
 *    Primary flag (at most one per organization, enforced by the database) and a Billing flag. A person can be a POC
 *    at several organizations; "Find existing person" links the existing record and never creates a duplicate.
 *    A NEW person needs first name, last name and email.
 * 2. Other contacts on file: the people whose record names this organization (contacts.organization_id), as before,
 *    with "Make POC".
 *
 * Only name, job title, POC role, email and phone are shown here -- never membership, subscription, case or support
 * history (review: reusing a person as a POC must not expose that through the organization page).
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MagnifyingGlassIcon,
  PlusIcon,
  StarIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import Card from "../ui/Card";
import Modal from "../ui/Modal";
import Button from "../common/Button";
import Input from "../common/Input";
import { useToast } from "../ui/Toast";
import { useConfirmDialog } from "../ui/ConfirmDialog";
import { formatPhoneNumber } from "../../utils/formatters";
import type { Contact } from "../../types/contact.types";
import {
  POC_ROLES,
  createOrganizationPoc,
  describePocError,
  getOrganizationPocs,
  removeOrganizationPoc,
  searchPocCandidates,
  setOrganizationPoc,
} from "../../services/organizationPeople.service";
import type {
  OrganizationPoc,
  PocCandidate,
  PocFlags,
  PocRole,
} from "../../services/organizationPeople.service";

interface OrganizationPocsTabProps {
  organizationId: string;
  /** contacts whose record names this organization (contacts.organization_id) */
  otherContacts: Contact[];
  canEdit: boolean;
  /** opens the pre-existing "Add Contact" modal (location association) */
  onAddOtherContact?: () => void;
}

const fullName = (p: {
  first_name?: string | null;
  last_name?: string | null;
}) => [p.first_name, p.last_name].filter(Boolean).join(" ") || "(no name)";

type Target =
  | { kind: "new" }
  | { kind: "link"; person?: PocCandidate | null }
  | { kind: "edit"; poc: OrganizationPoc };

const EMAIL_RE = /^[A-Za-z0-9._%+'-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const RoleFlagsFields: React.FC<{
  flags: PocFlags;
  onChange: (f: PocFlags) => void;
  currentPrimaryName?: string | null;
}> = ({ flags, onChange, currentPrimaryName }) => (
  <div className="space-y-3">
    <div>
      <label
        htmlFor="poc-role"
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        POC role at this organization
      </label>
      <select
        id="poc-role"
        value={flags.poc_role ?? ""}
        onChange={(e) =>
          onChange({
            ...flags,
            poc_role: (e.target.value || null) as PocRole | null,
          })
        }
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      >
        <option value="">— Not set —</option>
        {POC_ROLES.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>
    </div>
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4"
        checked={flags.is_primary}
        onChange={(e) => onChange({ ...flags, is_primary: e.target.checked })}
      />
      <span>
        <span className="font-medium text-gray-900">Primary contact</span>
        <span className="block text-gray-500">
          One per organization.
          {currentPrimaryName && flags.is_primary
            ? ` ${currentPrimaryName} will stop being the primary contact.`
            : ""}
        </span>
      </span>
    </label>
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4"
        checked={flags.is_billing_contact}
        onChange={(e) =>
          onChange({ ...flags, is_billing_contact: e.target.checked })
        }
      />
      <span>
        <span className="font-medium text-gray-900">Billing contact</span>
        <span className="block text-gray-500">
          An organization can have more than one.
        </span>
      </span>
    </label>
  </div>
);

const PocModal: React.FC<{
  organizationId: string;
  target: Target | null;
  pocs: OrganizationPoc[];
  onClose: () => void;
}> = ({ organizationId, target, pocs, onClose }) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [mode, setMode] = useState<"link" | "new">("link");
  const [flags, setFlags] = useState<PocFlags>({
    poc_role: null,
    is_primary: false,
    is_billing_contact: false,
  });
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [picked, setPicked] = useState<PocCandidate | null>(null);
  const [person, setPerson] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    title: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [existingId, setExistingId] = useState<string | null>(null);

  useEffect(() => {
    if (!target) {
      return;
    }
    setServerError(null);
    setExistingId(null);
    setErrors({});
    setQuery("");
    setSubmitted("");
    setPerson({
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      title: "",
    });
    if (target.kind === "edit") {
      setFlags({
        poc_role: target.poc.poc_role,
        is_primary: target.poc.is_primary,
        is_billing_contact: target.poc.is_billing_contact,
      });
      setPicked(null);
    } else {
      setFlags({
        poc_role: null,
        is_primary: pocs.length === 0,
        is_billing_contact: false,
      });
      setMode(target.kind === "new" ? "new" : "link");
      setPicked(target.kind === "link" ? (target.person ?? null) : null);
    }
  }, [target, pocs.length]);

  const {
    data: results = [],
    isFetching,
    isError: searchFailed,
  } = useQuery({
    queryKey: ["poc-candidates", organizationId, submitted],
    queryFn: () => searchPocCandidates(submitted, organizationId),
    enabled: !!target && mode === "link" && submitted.trim().length >= 3,
    retry: false,
  });

  const currentPrimary = pocs.find((p) => p.is_primary);
  const currentPrimaryName =
    currentPrimary &&
    !(
      target?.kind === "edit" &&
      target.poc.contact_id === currentPrimary.contact_id
    ) &&
    !(picked && picked.contact_id === currentPrimary.contact_id)
      ? fullName(currentPrimary)
      : null;

  const done = (msg: string) => {
    queryClient.invalidateQueries({
      queryKey: ["organization-pocs", organizationId],
    });
    queryClient.invalidateQueries({ queryKey: ["contacts", organizationId] });
    showToast("success", msg);
    onClose();
  };

  const save = useMutation({
    mutationFn: async () => {
      setServerError(null);
      setExistingId(null);
      if (target?.kind === "edit") {
        await setOrganizationPoc(organizationId, target.poc.contact_id, flags);
        return `Updated ${fullName(target.poc)}`;
      }
      if (mode === "link") {
        if (!picked) {
          throw new Error("Choose a person from the search results.");
        }
        await setOrganizationPoc(organizationId, picked.contact_id, flags);
        return `${fullName(picked)} is now a point of contact`;
      }
      const e: Record<string, string> = {};
      if (!person.first_name.trim()) {
        e.first_name = "First name is required";
      }
      if (!person.last_name.trim()) {
        e.last_name = "Last name is required";
      }
      if (!person.email.trim()) {
        e.email = "Email is required for a new point of contact";
      } else if (!EMAIL_RE.test(person.email.trim())) {
        e.email = "Invalid email format";
      }
      setErrors(e);
      if (Object.keys(e).length) {
        throw new Error("");
      }
      await createOrganizationPoc(organizationId, { ...person, ...flags });
      return `${person.first_name} ${person.last_name} added as a point of contact`;
    },
    onSuccess: (msg) => done(msg),
    onError: (err: Error & { existingId?: string }) => {
      if (!err.message) {
        return; // field errors already shown
      }
      setServerError(describePocError(err.message));
      if (err.existingId) {
        setExistingId(err.existingId);
      }
    },
  });

  const linkExisting = () => {
    setMode("link");
    setQuery(person.email.trim());
    setSubmitted(person.email.trim());
    setServerError(null);
  };

  if (!target) {
    return null;
  }
  const title =
    target.kind === "edit"
      ? `Edit point of contact: ${fullName(target.poc)}`
      : "Add point of contact";

  return (
    <Modal isOpen={!!target} onClose={onClose} title={title} size="lg">
      <div className="space-y-5" data-testid="poc-modal">
        {target.kind !== "edit" && (
          <div className="flex gap-2" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "link"}
              onClick={() => setMode("link")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${mode === "link" ? "bg-primary-100 text-primary-800" : "text-gray-600 hover:bg-gray-100"}`}
            >
              Find existing person
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "new"}
              onClick={() => setMode("new")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${mode === "new" ? "bg-primary-100 text-primary-800" : "text-gray-600 hover:bg-gray-100"}`}
            >
              New person
            </button>
          </div>
        )}

        {target.kind !== "edit" && mode === "link" && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Search people already in the CRM by name, email or phone. Linking
              them here does not change their record or their links to other
              organizations.
            </p>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setSubmitted(query.trim());
                setPicked(null);
              }}
            >
              <input
                aria-label="Search people"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Name, email or phone"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <Button type="submit" variant="secondary" size="sm">
                <MagnifyingGlassIcon className="h-4 w-4 mr-1" />
                Search
              </Button>
            </form>
            {submitted.length > 0 && submitted.length < 3 && (
              <p className="text-sm text-gray-500">
                Type at least 3 characters.
              </p>
            )}
            {submitted.length >= 3 && (
              <div className="max-h-56 overflow-y-auto rounded border border-gray-200 divide-y">
                {isFetching ? (
                  <p className="p-3 text-sm text-gray-500">Searching…</p>
                ) : searchFailed ? (
                  <p className="p-3 text-sm text-red-700" role="alert">
                    The search failed. Try a more specific name, email or phone
                    number.
                  </p>
                ) : results.length === 0 ? (
                  <p className="p-3 text-sm text-gray-500">
                    No one matches. Use “New person” to add them.
                  </p>
                ) : (
                  results.map((c) => (
                    <label
                      key={c.contact_id}
                      className={`flex items-start gap-3 p-3 text-sm ${c.already_poc ? "opacity-60" : "cursor-pointer hover:bg-gray-50"}`}
                    >
                      <input
                        type="radio"
                        name="poc-pick"
                        className="mt-1"
                        disabled={c.already_poc}
                        checked={picked?.contact_id === c.contact_id}
                        onChange={() => setPicked(c)}
                      />
                      <span>
                        <span className="font-medium text-gray-900">
                          {fullName(c)}
                        </span>
                        {c.title && (
                          <span className="text-gray-500"> · {c.title}</span>
                        )}
                        <span className="block text-gray-600">
                          {[
                            c.email,
                            c.phone_number
                              ? formatPhoneNumber(c.phone_number)
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ") || "No email or phone"}
                        </span>
                        {c.already_poc && (
                          <span className="text-xs text-gray-500">
                            Already a point of contact here
                          </span>
                        )}
                      </span>
                    </label>
                  ))
                )}
              </div>
            )}
            {picked && (
              <p className="text-sm text-gray-700">
                Selected: <strong>{fullName(picked)}</strong>
              </p>
            )}
          </div>
        )}

        {target.kind !== "edit" && mode === "new" && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="First Name"
                required
                value={person.first_name}
                error={errors.first_name}
                onChange={(e) =>
                  setPerson({ ...person, first_name: e.target.value })
                }
              />
              <Input
                label="Last Name"
                required
                value={person.last_name}
                error={errors.last_name}
                onChange={(e) =>
                  setPerson({ ...person, last_name: e.target.value })
                }
              />
            </div>
            <Input
              label="Contact Email"
              type="email"
              required
              value={person.email}
              error={errors.email}
              onChange={(e) => setPerson({ ...person, email: e.target.value })}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Contact Phone"
                type="tel"
                value={person.phone_number}
                onChange={(e) =>
                  setPerson({ ...person, phone_number: e.target.value })
                }
              />
              <Input
                label="Job Title"
                value={person.title}
                placeholder="e.g. Store Manager"
                onChange={(e) =>
                  setPerson({ ...person, title: e.target.value })
                }
              />
            </div>
          </div>
        )}

        <RoleFlagsFields
          flags={flags}
          onChange={setFlags}
          currentPrimaryName={currentPrimaryName}
        />

        {serverError && (
          <div
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
          >
            {serverError}
            {existingId && (
              <button
                type="button"
                className="ml-2 font-medium underline"
                onClick={linkExisting}
              >
                Find and link the existing person
              </button>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={save.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            isLoading={save.isPending}
            disabled={target.kind !== "edit" && mode === "link" && !picked}
          >
            {target.kind === "edit"
              ? "Save"
              : mode === "link"
                ? "Link as point of contact"
                : "Add point of contact"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const OrganizationPocsTab: React.FC<OrganizationPocsTabProps> = ({
  organizationId,
  otherContacts,
  canEdit,
  onAddOtherContact,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const [target, setTarget] = useState<Target | null>(null);

  const {
    data: pocs = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["organization-pocs", organizationId],
    queryFn: () => getOrganizationPocs(organizationId),
  });

  const pocIds = useMemo(() => new Set(pocs.map((p) => p.contact_id)), [pocs]);
  const others = otherContacts.filter((c) => !pocIds.has(c.id));

  const makePrimary = useMutation({
    mutationFn: (p: OrganizationPoc) =>
      setOrganizationPoc(organizationId, p.contact_id, {
        poc_role: p.poc_role,
        is_primary: true,
        is_billing_contact: p.is_billing_contact,
      }),
    onSuccess: (_, p) => {
      queryClient.invalidateQueries({
        queryKey: ["organization-pocs", organizationId],
      });
      showToast("success", `${fullName(p)} is now the primary contact`);
    },
    onError: (e: Error) => showToast("error", describePocError(e.message)),
  });

  const remove = useMutation({
    mutationFn: (p: OrganizationPoc) => removeOrganizationPoc(p.link_id),
    onSuccess: (_, p) => {
      queryClient.invalidateQueries({
        queryKey: ["organization-pocs", organizationId],
      });
      showToast(
        "success",
        `${fullName(p)} is no longer a point of contact here`,
      );
    },
    onError: (e: Error) => showToast("error", describePocError(e.message)),
  });

  const handleRemove = async (p: OrganizationPoc) => {
    const ok = await confirm({
      title: "Remove point of contact?",
      message: `${fullName(p)} will no longer be a point of contact for this organization. Their contact record and any links to other organizations are kept.`,
      confirmText: "Remove",
      variant: "danger",
    });
    if (ok) {
      remove.mutate(p);
    }
  };

  const th =
    "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider";

  return (
    <div className="space-y-6" data-testid="pocs-tab">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Points of Contact
            </h2>
            <p className="text-sm text-gray-500">
              The people we work with at this business. Mark one as primary and
              any billing contacts.
            </p>
          </div>
          {canEdit && (
            <Button
              onClick={() => setTarget({ kind: "link" })}
              data-testid="add-poc"
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              Add point of contact
            </Button>
          )}
        </div>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : error ? (
          <p className="text-sm text-red-700">
            Points of contact could not be loaded.
          </p>
        ) : pocs.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-lg">
            <UserGroupIcon className="mx-auto h-10 w-10 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              No points of contact yet.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table
              className="min-w-full divide-y divide-gray-200"
              data-testid="poc-table"
            >
              <thead className="bg-gray-50">
                <tr>
                  <th className={th}>Name</th>
                  <th className={th}>POC Role</th>
                  <th className={th}>Email</th>
                  <th className={th}>Phone</th>
                  <th className={`${th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pocs.map((p) => (
                  <tr key={p.link_id} data-testid="poc-row">
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">
                        {fullName(p)}
                      </div>
                      {p.title && (
                        <div className="text-gray-500">{p.title}</div>
                      )}
                      <div className="mt-1 flex flex-wrap gap-1">
                        {p.is_primary && (
                          <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                            <StarIcon className="h-3 w-3" aria-hidden="true" />
                            Primary
                          </span>
                        )}
                        {p.is_billing_contact && (
                          <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                            Billing
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {p.poc_role || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 break-all">
                      {p.email || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {p.phone_number ? formatPhoneNumber(p.phone_number) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-sm whitespace-nowrap space-x-3">
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => navigate(`/contacts/${p.contact_id}`)}
                      >
                        View
                      </button>
                      {canEdit && (
                        <>
                          {!p.is_primary && (
                            <button
                              className="text-amber-700 hover:text-amber-900"
                              onClick={() => makePrimary.mutate(p)}
                              disabled={makePrimary.isPending}
                            >
                              Make primary
                            </button>
                          )}
                          <button
                            className="text-indigo-600 hover:text-indigo-900"
                            onClick={() => setTarget({ kind: "edit", poc: p })}
                          >
                            Edit
                          </button>
                          <button
                            className="text-red-600 hover:text-red-800"
                            onClick={() => handleRemove(p)}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Other contacts on file
            </h3>
            <p className="text-sm text-gray-500">
              People whose contact record names this organization. Use “Make
              POC” to add them to the points of contact.
            </p>
          </div>
          {canEdit && onAddOtherContact && (
            <Button variant="secondary" size="sm" onClick={onAddOtherContact}>
              <PlusIcon className="h-4 w-4 mr-1" />
              Add contact
            </Button>
          )}
        </div>
        {others.length === 0 ? (
          <p className="text-sm text-gray-500">None.</p>
        ) : (
          <div className="overflow-x-auto">
            <table
              className="min-w-full divide-y divide-gray-200"
              data-testid="other-contacts-table"
            >
              <thead className="bg-gray-50">
                <tr>
                  <th className={th}>Name</th>
                  <th className={th}>Email</th>
                  <th className={th}>Phone</th>
                  <th className={`${th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {others.map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {fullName(c)}
                      {c.title && (
                        <span className="block font-normal text-gray-500">
                          {c.title}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 break-all">
                      {c.email || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {c.phone_number ? formatPhoneNumber(c.phone_number) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-sm whitespace-nowrap space-x-3">
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => navigate(`/contacts/${c.id}`)}
                      >
                        View
                      </button>
                      <button
                        className="text-indigo-600 hover:text-indigo-900"
                        onClick={() => navigate(`/contacts/${c.id}?edit=true`)}
                      >
                        Edit
                      </button>
                      {canEdit && (
                        <button
                          className="text-amber-700 hover:text-amber-900"
                          onClick={() =>
                            setTarget({
                              kind: "link",
                              person: {
                                contact_id: c.id,
                                first_name: c.first_name ?? null,
                                last_name: c.last_name ?? null,
                                email: c.email ?? null,
                                phone_number: c.phone_number ?? null,
                                title: c.title ?? null,
                                already_poc: false,
                              },
                            })
                          }
                        >
                          Make POC
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <PocModal
        organizationId={organizationId}
        target={target}
        pocs={pocs}
        onClose={() => setTarget(null)}
      />
      <ConfirmDialogComponent />
    </div>
  );
};

export default OrganizationPocsTab;
